import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// It's good practice to have youtube player types.
// You can install them with `npm install --save-dev @types/youtube`
// For now, we'll use a simplified interface.
interface YTPlayer {
  getCurrentTime: () => number;
  destroy: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Allow other properties
}

interface VideoModalProps {
  isOpen: boolean;
  onClose: (currentTime?: number) => void;
  video: {
    title: string;
    videoId: string;
    IframeUrl: string;
  } | null;
}

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, video }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const iframeId = `youtube-player-${video?.videoId}`;

  const handleClose = () => {
    console.log('VideoModal handleClose called'); // Debug log
    try {
      const currentTime = playerRef.current?.getCurrentTime();
      console.log('Current time:', currentTime); // Debug log
      onClose(currentTime);
    } catch (error) {
      console.error('Error getting current time:', error);
      onClose(); // Fallback without time
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';

      const scriptId = 'youtube-iframe-api';
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;

      const setupPlayer = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const YT = (window as any).YT;
        if (YT && YT.Player) {
          playerRef.current = new YT.Player(iframeId, {});
        }
      };

      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName('script')[0];

        if (firstScriptTag?.parentNode) {
          firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
        } else {
          document.head.appendChild(script);
        }
        
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).onYouTubeIframeAPIReady = () => {
          setupPlayer();
        };
      } else {
        setupPlayer();
      }

    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isOpen, video?.videoId]);

  if (!isOpen || !video) return null;

  return (
    <div 
    style={{
      background: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(4.5px) saturate(100%)',
      WebkitBackdropFilter: 'blur(5px) saturate(100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      borderRadius: '10px'
    }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div 
        ref={modalRef}
        className="relative w-full max-w-6xl bg-white rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            console.log('Close button clicked'); // Debug log
            handleClose();
          }}
          style={{
            background: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(3px) saturate(100%)',
            WebkitBackdropFilter: 'blur(5px) saturate(100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            color: 'white'
          }}
          className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-colors hover:bg-gray-600"
          aria-label="Close video"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Video iframe */}
        <div className="relative aspect-video bg-black">
          <iframe
            id={iframeId}
            src={video.IframeUrl}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export default VideoModal; 